import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../../../services/Logger';

// Simple SVG Icons as components
const DownloadIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const ClipboardCopyIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const EyeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const VolumeUpIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M9 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ExternalLinkIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

interface MediaAttachment {
  file_path: string;
  media_type: 'image' | 'video' | 'audio';
  filename: string;
  content_type: string;
  model_used?: string;
  generated_at?: string;
  metadata?: Record<string, any>;
}

interface MediaDisplayProps {
  media: MediaAttachment;
  theme?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  showMetadata?: boolean;
  className?: string;
}

const MediaDisplay: React.FC<MediaDisplayProps> = ({
  media,
  theme = 'light',
  size = 'md',
  showMetadata = false,
  className = ''
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Generate permanent proxy URL for display
  const getDisplayUrl = (filePath: string): string => {
    const apiBase = window.location.origin.includes('localhost')
      ? 'http://localhost:8080'
      : window.location.origin;
    return `${apiBase}/api/image?path=${encodeURIComponent(filePath)}`;
  };

  const handleDownload = async () => {
    try {
      // Download through /storage/download-media — serves the file with the correct
      // Content-Type so browsers save it properly instead of treating it as HTML.
      const orgId = (window as any).__photon_org_id ||
        (await import('../../../store/userStore')).default.getState().orgId;
      const proxyUrl = `/storage/download-media?file_path=${encodeURIComponent(media.file_path)}&org_id=${encodeURIComponent(orgId)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = media.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      logger.error('MediaDisplay', 'Download failed', { error: err instanceof Error ? err.message : err });
      setError('Failed to download file');
    }
  };

  const handleCopyUrl = async () => {
    try {
      const displayUrl = getDisplayUrl(media.file_path);
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy URL');
    }
  };

  const handleCopyFile = async () => {
    try {
      const displayUrl = getDisplayUrl(media.file_path);
      const response = await fetch(displayUrl);
      const blob = await response.blob();

      if (navigator.clipboard && 'write' in navigator.clipboard) {
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob })
        ]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback: copy URL instead
        await handleCopyUrl();
      }
    } catch (err) {
      // Fallback to copying URL
      await handleCopyUrl();
    }
  };

  const handleLoad = () => setIsLoading(false);
  const handleError = () => {
    setIsLoading(false);
    setError('Failed to load media');
  };

  const sizeClasses = {
    sm: 'max-w-xs max-h-48',
    md: 'max-w-md max-h-96',
    lg: 'max-w-2xl max-h-[32rem]'
  };

  const displayUrl = getDisplayUrl(media.file_path);

  return (
    <div className={`relative group ${sizeClasses[size]} ${className}`}>
      {/* Media Content */}
      <div className={`
        relative rounded-xl overflow-hidden shadow-lg
        ${theme === 'dark' ? 'bg-fill-gray-800 border-edge-gray-700' : 'bg-fill-white border-edge-gray-200'}
        border transition-all duration-300 group-hover:shadow-xl
      `}>

        {/* Error State */}
        {error && (
          <div className={`
            p-4 text-center
            ${theme === 'dark' ? 'text-ink-red-400 bg-fill-red-900/20' : 'text-ink-red-600 bg-fill-red-50'}
          `}>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Image Display */}
        {media.media_type === 'image' && !error && (
          <div className="relative">
            {isLoading && (
              <div className={`aspect-square w-full animate-pulse rounded-xl ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-200'}`} />
            )}
            <img
              src={displayUrl}
              alt={media.filename}
              onLoad={handleLoad}
              onError={handleError}
              className={`w-full h-auto object-contain cursor-zoom-in${isLoading ? ' hidden' : ''}`}
              onClick={() => setShowFullscreen(true)}
            />

            {/* Image Overlay */}
            {!isLoading && (
              <div className="absolute inset-0 bg-fill-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ opacity: 1, scale: 1 }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  <EyeIcon className="h-8 w-8 text-ink-white drop-shadow-lg" />
                </motion.div>
              </div>
            )}
          </div>
        )}

        {/* Video Display */}
        {media.media_type === 'video' && !error && (
          <div className="relative">
            {isLoading && (
              <div className={`aspect-video w-full animate-pulse ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-200'}`} />
            )}
            <video
              ref={videoRef}
              src={displayUrl}
              onLoadedData={handleLoad}
              onError={handleError}
              controls
              className={`w-full h-auto${isLoading ? ' hidden' : ''}`}
              preload="metadata"
            >
              Your browser does not support video playback.
            </video>
          </div>
        )}

        {/* Audio Display */}
        {media.media_type === 'audio' && !error && (
          isLoading ? (
            <div className={`p-6 flex items-center space-x-4 ${theme === 'dark' ? 'bg-fill-gray-800' : 'bg-fill-gray-50'}`}>
              <div className={`h-8 w-8 rounded-full animate-pulse flex-shrink-0 ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-200'}`} />
              <div className="flex-1 space-y-2">
                <div className={`h-8 w-full rounded animate-pulse ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-200'}`} />
                <div className={`h-3 w-32 rounded animate-pulse ${theme === 'dark' ? 'bg-fill-gray-600' : 'bg-fill-gray-300'}`} />
              </div>
            </div>
          ) : (
            <div className={`
              p-6 flex items-center space-x-4
              ${theme === 'dark' ? 'bg-fill-gray-800' : 'bg-fill-gray-50'}
            `}>
              <div className="flex-shrink-0">
                <VolumeUpIcon className={`
                  h-8 w-8
                  ${theme === 'dark' ? 'text-ink-gray-400' : 'text-ink-gray-500'}
                `} />
              </div>
              <div className="flex-1">
                <audio
                  ref={audioRef}
                  src={displayUrl}
                  onLoadedData={handleLoad}
                  onError={handleError}
                  controls
                  className="w-full"
                  preload="metadata"
                >
                  Your browser does not support audio playback.
                </audio>
                <p className={`
                  text-sm mt-2
                  ${theme === 'dark' ? 'text-ink-gray-400' : 'text-ink-gray-600'}
                `}>
                  {media.filename}
                </p>
              </div>
            </div>
          )
        )}

        {/* Action Buttons Overlay */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex space-x-2">
            {/* Copy Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={media.media_type === 'image' ? handleCopyFile : handleCopyUrl}
              className={`
                p-2 rounded-lg backdrop-blur-sm transition-all duration-200
                ${theme === 'dark'
                  ? 'bg-fill-gray-900/80 hover:bg-fill-gray-900 text-ink-white'
                  : 'bg-fill-white/80 hover:bg-fill-white text-ink-gray-700'
                }
                shadow-lg hover:shadow-xl
              `}
              title={media.media_type === 'image' ? 'Copy image' : 'Copy URL'}
            >
              {copied ? (
                <CheckIcon className="h-4 w-4 text-ink-green-500" />
              ) : (
                <ClipboardCopyIcon className="h-4 w-4" />
              )}
            </motion.button>

            {/* Download Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDownload}
              className={`
                p-2 rounded-lg backdrop-blur-sm transition-all duration-200
                ${theme === 'dark'
                  ? 'bg-fill-gray-900/80 hover:bg-fill-gray-900 text-ink-white'
                  : 'bg-fill-white/80 hover:bg-fill-white text-ink-gray-700'
                }
                shadow-lg hover:shadow-xl
              `}
              title="Download file"
            >
              <DownloadIcon className="h-4 w-4" />
            </motion.button>

            {/* External Link Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open(displayUrl, '_blank')}
              className={`
                p-2 rounded-lg backdrop-blur-sm transition-all duration-200
                ${theme === 'dark'
                  ? 'bg-fill-gray-900/80 hover:bg-fill-gray-900 text-ink-white'
                  : 'bg-fill-white/80 hover:bg-fill-white text-ink-gray-700'
                }
                shadow-lg hover:shadow-xl
              `}
              title="Open in new tab"
            >
              <ExternalLinkIcon className="h-4 w-4" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Metadata */}
      {showMetadata && !error && (
        <div className={`
          mt-3 p-3 rounded-lg text-xs
          ${theme === 'dark' ? 'bg-fill-gray-800 text-ink-gray-400' : 'bg-fill-gray-50 text-ink-gray-600'}
        `}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="font-medium">Type:</span> {media.media_type}
            </div>
            <div>
              <span className="font-medium">Size:</span> {media.content_type}
            </div>
            {media.model_used && (
              <div className="col-span-2">
                <span className="font-medium">Model:</span> {media.model_used}
              </div>
            )}
            {media.generated_at && (
              <div className="col-span-2">
                <span className="font-medium">Generated:</span> {new Date(media.generated_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fullscreen Modal for Images */}
      <AnimatePresence>
        {showFullscreen && media.media_type === 'image' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-fill-black bg-opacity-90 flex items-center justify-center p-4"
            onClick={() => setShowFullscreen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-full max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={displayUrl}
                alt={media.filename}
                className="max-w-full max-h-full object-contain"
              />
              <button
                onClick={() => setShowFullscreen(false)}
                className="absolute top-4 right-4 text-ink-on-color bg-fill-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Copy Success Notification */}
      <AnimatePresence>
        {copied && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute bottom-2 left-2 bg-fill-green-700 text-ink-on-color px-3 py-1 rounded-lg text-sm shadow-lg"
          >
            Copied!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaDisplay;
